import type { BillingStatusResponse } from '@spoofio/shared/types'
import type { OrganizationStatus } from '@spoofio/shared/types'
import { Badge } from '@/components/ui'
import { formatDate } from '@/utils/format-date'

type PlanSummaryProps = {
  billingStatus: BillingStatusResponse
}

const STATUS_LABELS: Record<OrganizationStatus, string> = {
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past Due',
  canceled: 'Canceled',
  inactive: 'Inactive',
  suspended: 'Suspended',
}

const STATUS_VARIANTS: Record<OrganizationStatus, 'success' | 'danger' | 'warning' | 'primary' | 'gray'> = {
  trial: 'primary',
  active: 'success',
  past_due: 'warning',
  canceled: 'danger',
  inactive: 'gray',
  suspended: 'danger',
}

export function PlanSummary({ billingStatus }: PlanSummaryProps) {
  const { organization, plan, usage } = billingStatus

  return (
    <div className="card">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Subscription Plan
          </h3>
          <Badge variant={STATUS_VARIANTS[organization.status]}>
            {STATUS_LABELS[organization.status]}
          </Badge>
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Plan</dt>
            <dd className="mt-1 text-sm text-gray-900 capitalize">
              {plan.planType}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {STATUS_LABELS[organization.status]}
            </dd>
          </div>
          {plan.currentPeriodStart && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Period Start</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(plan.currentPeriodStart)}
              </dd>
            </div>
          )}
          {plan.currentPeriodEnd && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Period End</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(plan.currentPeriodEnd)}
              </dd>
            </div>
          )}
          {organization.canceledAt && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Canceled At</dt>
              <dd className="mt-1 text-sm text-danger-600">
                {formatDate(organization.canceledAt)}
              </dd>
            </div>
          )}
          {organization.deletionScheduledAt && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Deletion Scheduled</dt>
              <dd className="mt-1 text-sm text-danger-600">
                {formatDate(organization.deletionScheduledAt)}
              </dd>
            </div>
          )}
        </dl>

        {/* Usage Meters */}
        <div className="mt-6 space-y-4">
          <h4 className="text-sm font-medium text-gray-900">Usage</h4>
          <UsageMeter
            label="Domains"
            current={usage.currentDomainCount}
            limit={plan.effectiveDomainLimit}
          />
          <UsageMeter
            label="Canaries"
            current={usage.currentCanaryCount}
            limit={plan.effectiveCanaryLimit}
          />
          {plan.ffiecEnhancedCanaries > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">FFIEC Enhanced</span>
              <span className="text-gray-900">
                {plan.ffiecEnhancedCanaries} {plan.ffiecEnhancedCanaries === 1 ? 'canary' : 'canaries'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type UsageMeterProps = {
  label: string
  current: number
  limit: number
}

function UsageMeter({ label, current, limit }: UsageMeterProps) {
  const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0
  const isNearLimit = percentage >= 80
  const isAtLimit = current >= limit

  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className={isAtLimit ? 'text-danger-600 font-medium' : 'text-gray-900'}>
          {current} / {limit}
        </span>
      </div>
      <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${
            isAtLimit
              ? 'bg-danger-500'
              : isNearLimit
                ? 'bg-warning-500'
                : 'bg-primary-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}
